import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Grid, Paper, Typography, Box, Button, Card,
    CardContent, CardActions, Chip, CircularProgress, Avatar, Divider
} from '@mui/material';
import {
    CalendarMonth, LocalHospital, ArrowBack, Cancel,
    CheckCircle, Pending, Schedule
} from '@mui/icons-material';
import { appointmentAPI } from '../services/api';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';

const MyAppointments = () => {
    const navigate = useNavigate();
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAppointments();
    }, []);

    const fetchAppointments = async () => {
        try {
            const userId = localStorage.getItem('userId');
            const response = await appointmentAPI.getPatientAppointments(userId);
            setAppointments(response.data);
        } catch (error) {
            toast.error('Failed to load appointments');
        } finally {
            setLoading(false);
        }
    };

    // ✅ PDF Download Function
    const downloadPrescription = (appointment) => {
        const pdf = new jsPDF();

        pdf.setFontSize(20);
        pdf.text('MediBook Pro - Prescription', 20, 20);

        pdf.setFontSize(12);
        pdf.text(`Doctor: Dr. ${appointment.doctor?.user?.name}`, 20, 40);
        pdf.text(`Specialization: ${appointment.doctor?.specialization}`, 20, 50);

        pdf.text(`Patient: ${appointment.patient?.user?.name || 'N/A'}`, 20, 70);
        pdf.text(`Date: ${appointment.appointmentDate}`, 20, 80);

        pdf.text('Prescription:', 20, 100);

        pdf.setFontSize(11);
        const prescriptionText = appointment.prescription || 'No prescription available';
        const lines = pdf.splitTextToSize(prescriptionText, 170);
        pdf.text(lines, 20, 110);

        pdf.setFontSize(10);
        pdf.text('Generated by MediBook Pro', 20, 280);
        pdf.text(new Date().toLocaleDateString(), 160, 280);

        pdf.save(`prescription_${appointment.id}.pdf`);
    };

    const handleCancelAppointment = async (id) => {
        if (!window.confirm('Are you sure you want to cancel this appointment?')) return;

        try {
            await appointmentAPI.cancel(id);
            toast.success('Appointment cancelled');
            fetchAppointments();
        } catch (error) {
            toast.error('Failed to cancel appointment');
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'PENDING': return 'warning';
            case 'CONFIRMED': return 'info';
            case 'COMPLETED': return 'success';
            case 'CANCELLED': return 'error';
            default: return 'default';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'PENDING': return <Pending />;
            case 'CONFIRMED': return <Schedule />;
            case 'COMPLETED': return <CheckCircle />;
            case 'CANCELLED': return <Cancel />;
            default: return <Pending />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/patient/dashboard')}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h4">📅 My Appointments</Typography>
            </Box>

            {appointments.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        No appointments yet
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate('/book-appointment')}
                        sx={{ mt: 2 }}
                    >
                        Book Your First Appointment
                    </Button>
                </Paper>
            ) : (
                <Grid container spacing={3}>
                    {appointments.map((appointment) => (
                        <Grid item xs={12} md={6} key={appointment.id}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                                                <LocalHospital />
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6">
                                                    Dr. {appointment.doctor?.user?.name || 'Unknown'}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary">
                                                    {appointment.doctor?.specialization || 'General'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Chip
                                            icon={getStatusIcon(appointment.status)}
                                            label={appointment.status}
                                            color={getStatusColor(appointment.status)}
                                            size="small"
                                        />
                                    </Box>

                                    <Divider sx={{ my: 1 }} />

                                    <Typography variant="body2">
                                        📅 Date: {appointment.appointmentDate}
                                    </Typography>
                                    <Typography variant="body2">
                                        ⏰ Time: {appointment.appointmentTime}
                                    </Typography>
                                    <Typography variant="body2">
                                        💰 Fee: ₹{appointment.fee || 500}
                                    </Typography>

                                    {appointment.reason && (
                                        <Typography variant="body2" sx={{ mt: 1 }}>
                                            📝 Reason: {appointment.reason}
                                        </Typography>
                                    )}

                                    {appointment.prescription && (
                                        <Typography
                                            variant="body2"
                                            sx={{ mt: 1, color: 'success.main' }}
                                        >
                                            💊 Prescription: {appointment.prescription}
                                        </Typography>
                                    )}
                                </CardContent>

                                <CardActions>
                                    {(appointment.status === 'PENDING' ||
                                        appointment.status === 'CONFIRMED') && (
                                        <Button
                                            size="small"
                                            color="error"
                                            onClick={() =>
                                                handleCancelAppointment(appointment.id)
                                            }
                                        >
                                            Cancel
                                        </Button>
                                    )}

                                    {appointment.status === 'COMPLETED' &&
                                        appointment.prescription && (
                                            <Button
                                                size="small"
                                                color="primary"
                                                onClick={() =>
                                                    downloadPrescription(appointment)
                                                }
                                            >
                                                Download PDF
                                            </Button>
                                        )}
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Container>
    );
};

export default MyAppointments;
